import React, { useEffect, useImperativeHandle, useRef, useState } from 'react'
import {
  Award01Icon,
  ChevronDownIcon,
  CircleIcon,
  GraduationHat02Icon,
  CheckCircleIcon,
} from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { mergeRefs } from 'shared/hooks/use-merged-ref'
import { useScrollIntoView } from 'shared/hooks/use-scroll-into-view'
import { cwd } from 'shared/utils/class-name-with-modifiers'
import useCertificateOfCompletion from 'modules/course/hooks/use-certificate-of-completion'
import { useCourseData } from 'modules/course/hooks/use-course-data'
import { useCourseMenu } from 'modules/course/hooks/use-course-menu'
import useStepRouter from 'modules/course/hooks/use-step-router'
import { CourseStepEnum } from 'modules/course/types/course-step-type'
import { addPublicCustomClassNamePrefix } from 'modules/course/utils/add-class-prefix'
import CourseAvailabilityIcon from './availability-icon'
import { CourseLectureAssignmentStatus } from './course-lecture-assignment-status'

export const CourseModuleList = React.forwardRef<HTMLUListElement>(
  function CourseModuleList(_props, forwardedRef) {
    const { t } = useTranslation()

    const [isModuleOpened, setIsModuleOpened] = useState<Array<boolean>>([])

    const skipScroll = useRef<boolean>(false)
    const ref = useRef<HTMLUListElement>(null)
    const { scrollIntoView, scrollableRef, targetRef } = useScrollIntoView<
      HTMLLIElement,
      HTMLUListElement
    >({
      isList: true,
    })

    const { isCertificateAvailable, certificatePageUrl, isOnCertificatePage } =
      useCertificateOfCompletion()

    useImperativeHandle(forwardedRef, () => ref.current as HTMLUListElement)

    const { isCurrentQuiz, getLectureUrl, getQuizUrl, step } = useStepRouter()

    const { courseData } = useCourseData()
    const { courseMenu } = useCourseMenu({ courseId: courseData?.id })

    const onToggleModule = (index: number): void => {
      setIsModuleOpened(prev => {
        const state = [...prev]
        state[index] = !state[index]
        return state
      })
    }

    useEffect(() => {
      if (courseMenu) {
        const moduleIndex = courseMenu.findIndex(
          module =>
            module.lectures.some(({ id }) => id === step?.id) || step?.id === module.quiz?.id,
        )

        if (moduleIndex !== -1) {
          const state = courseMenu.map(() => false)
          state[moduleIndex] = true
          setIsModuleOpened(state)
        }
      }
    }, [courseMenu, step?.id, step?.type])

    useEffect(() => {
      if (!skipScroll.current && courseMenu && ref.current && targetRef.current) {
        scrollIntoView({ alignment: 'center' })
      } else {
        skipScroll.current = false
      }
    }, [courseMenu, step?.id, step?.type])

    return (
      <ul
        ref={mergeRefs(ref, scrollableRef)}
        className={twMerge(
          'mt-9 flex customScroll flex-col gap-8 overflow-y-auto',
          addPublicCustomClassNamePrefix('course-modules-list'),
        )}
      >
        {courseMenu?.map((module, index) => {
          const {
            available: isModuleAvailable,
            shouldShowQuiz,
            lectures,
            isCompleted: isModuleCompleted,
            quiz,
          } = module

          const isQuizCurrent = isCurrentQuiz(quiz)

          return (
            <li
              key={index}
              className={cwd(
                addPublicCustomClassNamePrefix('course-modules-list__module-container'),
                {
                  ['opened']: isModuleOpened[index],
                  ['available']: isModuleAvailable,
                  ['completed']: isModuleCompleted,
                },
              )}
            >
              <div
                className={twMerge(
                  'flex items-center gap-1',
                  courseData?.displayModuleTitles ? 'mb-1' : 'hidden',
                  addPublicCustomClassNamePrefix('course-modules-list__module-title'),
                )}
              >
                <button
                  type="button"
                  className={twMerge(
                    'mt-0 flex items-center gap-1 text-left text-lg leading-[22px] wrap-break-word word-break-word',
                    isModuleAvailable
                      ? 'text-THEME-primary hover:text-THEME-hover-primary'
                      : 'text-THEME-disabled',
                    !isModuleCompleted && 'font-bold',
                  )}
                  onClick={() => onToggleModule(index)}
                  data-test-id={`course-menu-module-${module.name.replaceAll(' ', '-')}`}
                >
                  <ChevronDownIcon
                    className={twMerge('size-5 shrink-0', isModuleOpened[index] && 'rotate-180')}
                  />
                  {module.name}
                </button>
                {!isModuleAvailable && (
                  <CourseAvailabilityIcon
                    unavailabilityReason={module.unavailabilityReason}
                    lectureId={module.lectures?.[0]?.id}
                  />
                )}
              </div>
              <div
                // NOTE: this is hack for animation
                className={twMerge(
                  'grid transition-[grid-template-rows] duration-200',
                  isModuleOpened[index] || !courseData?.displayModuleTitles
                    ? 'grid-rows-[1fr]'
                    : 'grid-rows-[0fr]',
                )}
              >
                <ul
                  className={twMerge('overflow-hidden', courseData?.displayModuleTitles && 'ml-4')}
                >
                  {lectures.map(lecture => {
                    const isLectureCurrent =
                      step?.type === CourseStepEnum.lecture && step.id === lecture.id

                    return (
                      <li
                        ref={isLectureCurrent ? targetRef : undefined}
                        key={lecture.id}
                        className={cwd(
                          addPublicCustomClassNamePrefix('course-modules-list__lecture'),
                          {
                            ['available']: lecture.available,
                            ['completed']: lecture.completed,
                            ['selected']: isLectureCurrent,
                          },
                        )}
                        data-test-id={`course-menu-lecture-${lecture.id}`}
                      >
                        <LinkWithoutPrefetch
                          className={twMerge(
                            'text-left text-base wrap-break-word word-break-word',
                            lecture.available
                              ? isLectureCurrent
                                ? 'text-THEME-secondary hover:text-THEME-hover-secondary'
                                : 'text-THEME-primary hover:text-THEME-hover-primary'
                              : 'text-THEME-disabled',
                            !lecture.completed && 'font-semibold',
                            'mt-3 flex items-center gap-2',
                          )}
                          href={getLectureUrl(lecture.id)}
                          disabled={!lecture.available}
                          scroll={false}
                        >
                          {lecture.completed ? (
                            <CheckCircleIcon className="ml-[3px] size-5 shrink-0" />
                          ) : (
                            <CircleIcon className="ml-[3px] size-5 shrink-0" />
                          )}
                          <span>{lecture.name}</span>
                          {isModuleAvailable && !lecture.available && (
                            <CourseAvailabilityIcon
                              unavailabilityReason={lecture.unavailabilityReason}
                              lectureId={lecture.id}
                            />
                          )}
                        </LinkWithoutPrefetch>
                        {!!lecture.assignment && (
                          <CourseLectureAssignmentStatus lecture={lecture} />
                        )}
                      </li>
                    )
                  })}
                  {module.quiz && shouldShowQuiz && (
                    <li
                      ref={isQuizCurrent ? targetRef : undefined}
                      className={cwd('course-modules-list__quiz', {
                        ['available']: module.quiz.available,
                        ['completed']: module.quiz.completed,
                        ['selected']: isQuizCurrent,
                      })}
                    >
                      <LinkWithoutPrefetch
                        className={twMerge(
                          'text-base disabled:text-THEME-disabled',
                          module.quiz.available &&
                            (isQuizCurrent
                              ? 'text-THEME-secondary hover:text-THEME-hover-secondary'
                              : 'text-THEME-primary hover:text-THEME-hover-primary'),
                          !module.quiz.completed && 'font-semibold',
                          module.quiz.available && !isQuizCurrent && 'cursor-pointer',
                          'mt-4 flex items-center gap-1',
                        )}
                        href={getQuizUrl(module.quiz.id)}
                        onClick={() => {
                          skipScroll.current = true
                        }}
                        scroll={false}
                      >
                        <GraduationHat02Icon className="size-5 shrink-0" />
                        {t('course.quiz.label')}
                      </LinkWithoutPrefetch>
                    </li>
                  )}
                </ul>
              </div>
            </li>
          )
        })}
        {isCertificateAvailable && (
          <LinkWithoutPrefetch
            className={twMerge(
              'text-left text-base wrap-break-word word-break-word',
              isOnCertificatePage
                ? 'text-THEME-secondary hover:text-THEME-hover-secondary'
                : 'text-THEME-primary hover:text-THEME-hover-primary',
              'mt-3 flex items-center gap-2',
            )}
            href={certificatePageUrl}
          >
            <Award01Icon className="size-5" />
            {t('course.certificate.title')}
          </LinkWithoutPrefetch>
        )}
      </ul>
    )
  },
)
